import { useState, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useUser = () => {
  const { isLoading, error, sendRequest } = useHttp();
  const authCtx = useContext(AuthContext);
  const [successMessage, setSuccessMessage] = useState(null);

  const requestPasswordReset = async input => {
    try {
      setSuccessMessage(null);

      const body = { input };

      const result = await sendRequest({
        url: '/api/v1/user/request-password-reset',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      });

      if (result) {
        setSuccessMessage(result.message);
      }

      return result;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  };

  const resetPassword = async (resetToken, resetKey, newPassword) => {
    try {
      setSuccessMessage(null);

      const result = await sendRequest({
        url: '/api/v1/user/reset-password',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          resetToken,
          resetKey,
          newPassword,
        },
      });

      if (result) {
        setSuccessMessage(result.message);
      }

      return result;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const login = async (usernameOrRequest, password, isPrivateDevice) => {
    // TODO rewrite this
    try {
      let result;

      // Handle both parameter styles: individual params or request object
      if (typeof usernameOrRequest === 'object') {
        // Called with request object - use it directly
        result = await sendRequest(usernameOrRequest);
      } else {
        // Called with individual parameters
        const headers = authCtx.getAuthHeaders({ withAuth: false, withDevice: true });

        result = await sendRequest({
          url: '/api/v1/auth/login',
          method: 'POST',
          headers,
          body: {
            username: usernameOrRequest,
            password,
            isPrivateDevice: isPrivateDevice || undefined,
          },
        });
      }

      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const performLoginWithDeviceToken = async (username, password, deviceToken) => {
    try {
      const result = await sendRequest({
        url: '/api/v1/auth/login',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Device-Token': deviceToken,
        },
        body: {
          username: username,
          password: password,
          isPrivateDevice: false,
        },
      });
      return result;
    } catch (error) {
      console.error('Login with device token error:', error);
      throw error;
    }
  };

  const loginWithAuthHeaders = async (username, password, isPrivateDevice) => {
    try {
      const result = await sendRequest({
        url: '/api/v1/auth/login',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: false, withDevice: true }),
        body: {
          username: username,
          password: password,
          isPrivateDevice: isPrivateDevice || undefined,
        },
      });
      return result;
    } catch (error) {
      console.error('Login with auth headers error:', error);
      throw error;
    }
  };

  const rfidLogin = async (cardId, intermediateTokenData) => {
    try {
      const result = await sendRequest({
        url: '/api/v1/auth/rfid-login',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${intermediateTokenData.token}`,
          'Device-Token': authCtx.deviceToken,
        },
        body: { cardId },
      });

      return result;
    } catch (error) {
      console.error('RFID login error:', error);
      throw error;
    }
  };

  const getAllNewUsers = async () => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/newusers',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getAllNewUsers:', err);
      return [];
    }
  };

  const getNewUsersCount = async () => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/newusers/count',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getNewUsersCount:', err);
      return { count: 0 };
    }
  };

  const startPrintProcess = async (userId, branchId) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/user/newusers/${userId}/print`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: branchId ? { branchId } : {},
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser startPrintProcess:', err);
      throw err;
    }
  };

  // minimal user status list
  const getUserStatus = async ({ search = '', branchId = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (branchId) params.append('branchId', branchId);

      const responseData = await sendRequest({
        url: `/api/v1/user/status?${params.toString()}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData?.users || [];
    } catch (err) {
      console.error('error in useUser getUserStatus:', err);
      throw err;
    }
  };

  const migrateUsersFromCSV = async (csvData, format = 'withEmployeeId') => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/migrate-csv',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: { csvData, format },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser migrateUsersFromCSV:', err);
      throw err;
    }
  };

  const fetchUsers = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/user',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const deactivateUser = async ({ userId, immediate = true, plannedDeactivationAt = null }) => {
    try {
      const body = { immediate };
      if (!immediate && plannedDeactivationAt) body.plannedDeactivationAt = plannedDeactivationAt;
      const responseData = await sendRequest({
        url: `/api/v1/user/${userId}/deactivate`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body,
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser deactivateUser:', err);
      throw err;
    }
  };

  const deactivateAssignedBranchUser = async userId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/user/${userId}/deactivate-assigned-branches`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: { immediate: true },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser deactivateAssignedBranchUser:', err);
      throw err;
    }
  };

  const updateUser = async (userId, updateData) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/user/${userId}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: updateData,
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser updateUser:', err);
      throw err;
    }
  };

  const updateUserPreferences = async (preferences, options = {}) => {
    return new Promise(async (resolve, reject) => {
      try {
        const body = {};
        if (preferences && typeof preferences === 'object') {
          body.preferences = preferences;
        }
        if (
          Array.isArray(options.acknowledgeIntroductions) &&
          options.acknowledgeIntroductions.length > 0
        ) {
          body.acknowledgeIntroductions = options.acknowledgeIntroductions;
        }
        const responseData = await sendRequest({
          url: '/api/v1/user/preferences',
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authCtx.token,
          },
          body,
        });

        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getUserPreferences = async (token = null) => {
    try {
      const tokenToUse = token || authCtx.token;
      if (!tokenToUse) {
        console.warn('No token provided for getUserPreferences');
        return null;
      }

      const responseData = await sendRequest({
        url: '/api/v1/user/preferences',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokenToUse,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getUserPreferences:', err);
      return null;
    }
  };

  // Fetch the user's pre-configured default booking data (customer data + comment).
  // Only callable by users holding the `internalBooking:special:defaultCustomerData`
  // right; callers must guard accordingly to avoid a 403 auth-escalation prompt.
  const getDefaultBookingData = async () => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/default-booking-data',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData?.defaultBookingData || null;
    } catch (err) {
      console.error('error in useUser getDefaultBookingData:', err);
      return null;
    }
  };

  const getDevAdyenBypassToken = async () => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/dev/adyen-bypass-token',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getDevAdyenBypassToken:', err);
      throw err;
    }
  };

  const getMenuCapabilities = async (token = null) => {
    try {
      const tokenToUse = token || authCtx.token;
      if (!tokenToUse) {
        console.warn('No token provided for getMenuCapabilities');
        return null;
      }

      const responseData = await sendRequest({
        url: '/api/v1/user/menu-capabilities',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokenToUse,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getMenuCapabilities:', err);
      return null;
    }
  };

  // fetch unmatched timebutler users with filters
  const getUnmatchedTimebutlerUsers = async ({
    page = 1,
    limit = 20,
    search = '',
    branchId = '',
    department = '',
    showIgnored = false,
  } = {}) => {
    try {
      const params = new URLSearchParams();
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      if (search) params.append('search', search);
      if (branchId) params.append('branchId', branchId);
      if (department) params.append('department', department);
      if (showIgnored) params.append('showIgnored', 'true');

      const responseData = await sendRequest({
        url: `/api/v1/user/unmatched-timebutler?${params.toString()}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser getUnmatchedTimebutlerUsers:', err);
      throw err;
    }
  };

  // fetch list of distinct timebutler departments
  const getTimebutlerDepartments = async () => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/timebutler-departments',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData?.departments || [];
    } catch (err) {
      console.error('error in useUser getTimebutlerDepartments:', err);
      return [];
    }
  };

  // convert a timebutler user
  const convertTimebutlerUser = async timebutlerUserId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/user/convert-timebutler/${timebutlerUserId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser convertTimebutlerUser:', err);
      throw err;
    }
  };

  // ignore/unignore timebutler user
  const setTimebutlerUserIgnored = async (timebutlerUserId, ignore = true) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/user/timebutler-user/${timebutlerUserId}/ignore`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: { ignore },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser setTimebutlerUserIgnored:', err);
      throw err;
    }
  };

  // batch-activate all unmatched timebutler users for a branch
  const batchActivateBranch = async ({ branchId, includeIgnored = false }) => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/user/batch-activate-branch',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authCtx.token,
        },
        body: { branchId, includeIgnored },
      });
      return responseData;
    } catch (err) {
      console.error('error in useUser batchActivateBranch:', err);
      throw err;
    }
  };

  const getMyBranches = async () => {
    try {
      return await sendRequest({
        url: '/api/v1/user/my-branches',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    } catch (err) {
      console.error('error in useUser getMyBranches:', err);
      throw err;
    }
  };

  return {
    isLoading,
    error,
    successMessage,
    requestPasswordReset,
    resetPassword,
    login,
    performLoginWithDeviceToken,
    loginWithAuthHeaders,
    rfidLogin,
    getAllNewUsers,
    getNewUsersCount,
    startPrintProcess,
    getUserStatus,
    migrateUsersFromCSV,
    getUserPreferences,
    getDefaultBookingData,
    getMyBranches,
    getMenuCapabilities,
    fetchUsers,
    updateUserPreferences,
    getDevAdyenBypassToken,
    getUnmatchedTimebutlerUsers,
    convertTimebutlerUser,
    setTimebutlerUserIgnored,
    getTimebutlerDepartments,
    batchActivateBranch,
    getMyBranches,
    deactivateUser,
    deactivateAssignedBranchUser,
    updateUser,
  };
};

export default useUser;
